import React, {forwardRef, useImperativeHandle, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Divider} from '@blueprintjs/core';

import {IState} from '../../../../store/reducers';
import {useCurrentUser} from '../../../../hooks/use-user-current-workspace';
import {createWebhook, deleteWebhook, updateWebhook} from '../../../../store/actions/webhookActions';
import {Description, SelectNotificationRecipients, SelectNotificationType} from '../components';
import {TRIGGER_SOURCE} from '../constants';
import WebhookInput from './WebhookInput';

import './WebhookForm.scss';

interface WebhookFormProps {
  uid?: string;
  id?: number;
  source: TRIGGER_SOURCE;
  data: any | null;
  edit: boolean;
}

const WebhookForm = forwardRef(({
  uid,
  id,
  source,
  data,
  edit,
}: WebhookFormProps, ref: any) => {
  const users = useSelector((state: IState) => state.users.users);
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();

  const webhookRef = useRef<any>(null);
  const notificationRef = useRef<any>(null);
  const recipientsRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    onSave() {
      saveWebhook();
    },
    onDelete() {
      const {uid} = (data || {});

      uid && dispatch(deleteWebhook(uid));
    }
  }));

  const saveWebhook = () => {
    let payload: any = {
      token: webhookRef.current.getValue(),
      details: {
        notification: notificationRef.current.getValue(),
      },
      recipients: recipientsRef?.current?.getValue() || null,
    }

    if (edit) {
      const {uid} = (data || {});

      dispatch(updateWebhook(uid, payload));
    } else {
      payload = {
        ...payload,
        report_id: source === TRIGGER_SOURCE.REPORT ? id : undefined,
        active: true,
      };

      dispatch(createWebhook(payload as any));
    }
  };

  return (
    <div className="trigger-dialog__form">
      <Description>
        Use a webhook trigger to start the run of a workflow or a single task from an external app or process.
        You can also pass parameters via the URL to control the execution flow.<br />
        Whenever the webhook URL is called, a new run will be started.
        <a href="https://pushmetrics.io/docs/notebooks/webhook-trigger/" target="_blank"> Learn more</a>
      </Description>
      <Divider />
      <WebhookInput
        data={data}
        type={source}
        uid={uid}
        ref={webhookRef}
      />
      <Divider />
      <SelectNotificationType
        data={data?.details_json?.notification}
        ref={notificationRef}
      />
      <SelectNotificationRecipients
        users={users}
        currentUser={currentUser}
        data={data}
        edit={edit}
        ref={recipientsRef}
      />
    </div>
  );
});

export default WebhookForm;
